import React, { useState, useEffect, useRef } from 'react';

const PlayerSearchSelector = ({
  placeholder,
  items,
  onSelect,
  onSearch,
  initialItem = null,
  displayProperty = 'username',
  idProperty = 'id',
  imageProperty = 'image'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedItem, setSelectedItem] = useState(initialItem);
  const [isSearching, setIsSearching] = useState(false);
  const selectorRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Update search results when items change or component mounts
  useEffect(() => {
    setSearchResults(items || []);
  }, [items]);

  useEffect(() => {
    if (initialItem) {
      setSelectedItem(initialItem);
    }
  }, [initialItem]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clean up search timeout on unmount
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setShowResults(true);

    // If onSearch prop is provided, use debounced API search
    if (onSearch) {
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set loading state
      setIsSearching(true);

      // Debounce the search to avoid too many API calls
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          await onSearch(query);
        } finally {
          setIsSearching(false);
        }
      }, 300); // 300ms delay
    } else {
      // Fallback to local filtering if no onSearch prop
      filterResults(query);
    }
  };

  const filterResults = (query) => {
    if (!query || query.trim() === '') {
      setSearchResults(items || []); // Show all items when query is empty
      return;
    }

    const results = (items || []).filter(item => {
      // Try multiple possible username properties
      const displayValue = item[displayProperty] || item.username || item.name;

      if (!displayValue) {
        return false;
      }

      const matches = displayValue.toLowerCase().includes(query.toLowerCase());
      return matches;
    });

    setSearchResults(results);
  };

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setSearchQuery('');
    setShowResults(false);
    onSelect(item);
  };

  const handleClear = () => {
    setSelectedItem(null);
    setSearchQuery('');
    setShowResults(false);
    onSelect(null);
  };

  const handleFocus = () => {
    if (!searchQuery && !onSearch) {
      setSearchResults(items || []); // Show all items on focus if no search query and no API search
    }
    setShowResults(true);
  };

  return (
    <div className="search-selector" ref={selectorRef}>
      <div className="search-box">
        <input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={handleFocus}
        />

        {showResults && isSearching && (
          <div className="search-results">
            <div className="search-result-item no-results">
              Searching...
            </div>
          </div>
        )}

        {showResults && !isSearching && searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map(item => {
              const displayValue = item[displayProperty] || item.username || item.name || 'Unknown Player';

              return (
                <div
                  key={item[idProperty]}
                  className="search-result-item"
                  onClick={() => handleSelectItem(item)}
                >
                  {item[imageProperty] && (
                    <img
                      src={item[imageProperty]}
                      alt={displayValue}
                      className="result-image"
                      onError={(e) => { e.target.src = '/assets/images/placeholder.png' }}
                    />
                  )}
                  <span>{displayValue}</span>
                </div>
              );
            })}
          </div>
        )}

        {showResults && !isSearching && searchResults.length === 0 && searchQuery && searchQuery.length >= 2 && (
          <div className="search-results">
            <div className="search-result-item no-results">
              No players found matching "{searchQuery}"
            </div>
          </div>
        )}

        {showResults && !isSearching && searchQuery && searchQuery.length < 2 && (
          <div className="search-results">
            <div className="search-result-item no-results">
              Type at least 2 characters to search
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerSearchSelector;
